import { VariantDetail } from "@interfaces/mileage";

export const getFuelTypes = (variants: VariantDetail[]): string[] => {
  return [...new Set(variants.map((v) => v.fuelType?.toLowerCase()))];
};

export const getTransmissionTypes = (variants: VariantDetail[]): string[] => {
  return [...new Set(variants.map((v) => v.transmission?.toLowerCase()))];
};

export const getFilteredVariants = (
  variants: VariantDetail[],
  selectedFuelType: string | null,
  selectedTransmission: string | null
): VariantDetail[] => {
  let result = variants;

  if (selectedFuelType) {
    result = result.filter(
      (v) =>
        v.fuelType?.toLowerCase()?.trim() ===
        selectedFuelType.toLowerCase().trim()
    );
  }

  if (selectedTransmission) {
    result = result.filter(
      (v) =>
        v.transmission?.toLowerCase()?.trim() ===
        selectedTransmission.toLowerCase().trim()
    );
  }

  return result.sort((a, b) => {
    if (a.fuelType === "Petrol" && b.fuelType === "Diesel") return -1;
    if (a.fuelType === "Diesel" && b.fuelType === "Petrol") return 1;
    return 0;
  });
};

export const generateVariantKey = (variant: VariantDetail): string => {
  const baseKey = `${variant.name}-${variant.fuelType}-${variant.transmission}-${variant.mileage}-${variant.price}`;

  const cleanKey = baseKey
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\w\-\.]/g, "")
    .replace(/--+/g, "-")
    .replace(/^-+|-+$/g, "");

  return cleanKey;
};

export const createUniqueVariantKey = (
  variant: VariantDetail,
  usedKeys: Set<string>
): string => {
  const baseKey = generateVariantKey(variant);
  let uniqueKey = baseKey;
  let counter = 1;

  while (usedKeys.has(uniqueKey)) {
    uniqueKey = `${baseKey}-${counter}`;
    counter++;
  }

  usedKeys.add(uniqueKey);
  return uniqueKey;
};

interface AllVariantSlugs {
  uniqueSortedVariants?: {
    variantName: string;
    slug: string;
  }[];
}

interface DropdownVariant {
  id: string;
  name: string;
  slug: string;
}

export const createDropdownVariantsFromSlugs = (
  allVariantSlugs: AllVariantSlugs
): DropdownVariant[] => {
  return (
    allVariantSlugs.uniqueSortedVariants?.map((variant) => ({
      id: variant!.slug,
      name: variant!.variantName,
      slug: variant!.slug,
    })) || []
  );
};

export const getFirstAvailableVariantSlug = (
  allVariantSlugs: AllVariantSlugs | undefined,
  fallbackSlug: string
): string => {
  if (!allVariantSlugs) {
    return fallbackSlug;
  }

  return allVariantSlugs.uniqueSortedVariants?.[0]?.slug || fallbackSlug;
};

type VariantWithSlugAndStatus = {
  slug?: string;
  status?: string;
};

type ModelDetailsWithVariants = {
  allVariantSlugs?: {
    uniqueSortedVariants?: VariantWithSlugAndStatus[];
  };
};

export const getFirstSellableVariantSlug = (
  modelDetails: ModelDetailsWithVariants | undefined,
  fallbackSlug: string
): string => {
  const variants = modelDetails?.allVariantSlugs?.uniqueSortedVariants;
  if (!Array.isArray(variants) || variants.length === 0) {
    return fallbackSlug;
  }

  const firstSellable = variants.find((v) => v?.status === "sellable");
  if (firstSellable?.slug) {
    return firstSellable.slug;
  }

  return variants[0]?.slug || fallbackSlug;
};
